import axios from 'axios';
import type {
  PaginatedResponse, Document, DocumentType, DocumentTypeCreate, DocumentTypeUpdate,
  Collaborateur, CollaborateurWithStats, RGPDAlert, AlertsSummary, DashboardStats,
  ScanSource, ScanSourceCreate, AppSettings, User, UserCreate, UserUpdate,
} from './types';

export type { AppSettings, User };

const api = axios.create({
  baseURL: window.__RUNTIME_CONFIG__?.API_URL || import.meta.env.VITE_API_URL || 'http://localhost:8000',
  headers: { 'Content-Type': 'application/json' },
});

// ── Intercepteur requêtes : inject Bearer token ─────────────────────
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('acodoc_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Intercepteur réponses : 401 → page login ────────────────────────
api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem('acodoc_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

// ── Documents ───────────────────────────────────────────────────

export const documentsApi = {
  list: (params?: Record<string, any>) =>
    api.get<PaginatedResponse<Document>>('/api/v1/documents/', { params }),

  get: (id: string) =>
    api.get<Document>(`/api/v1/documents/${id}`),

  upload: (file: File, collaborateurId?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (collaborateurId) form.append('collaborateur_id', collaborateurId);
    return api.post('/api/v1/documents/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  delete: (id: string, hard = false) =>
    api.delete(`/api/v1/documents/${id}`, { params: { hard } }),

  reprocess: (id: string) =>
    api.post(`/api/v1/documents/${id}/reprocess`),

  validate: (id: string) =>
    api.post(`/api/v1/documents/${id}/validate`),

  getPreviewUrl: (id: string) =>
    api.get(`/api/v1/documents/${id}/preview`, { responseType: 'blob' }),

  update: (id: string, data: { collaborateur_id?: string | null; document_type_id?: string | null; sous_type?: string | null }) =>
    api.put<Document>(`/api/v1/documents/${id}`, data),

  correctField: (docId: string, fieldId: string, value: string) =>
    api.put(`/api/v1/documents/${docId}/extracted/${fieldId}`, { field_value: value }),

  addField: (docId: string, fieldName: string, value: string) =>
    api.post(`/api/v1/documents/${docId}/extracted`, { field_name: fieldName, field_value: value }),
};

// ── Document Types ──────────────────────────────────────────────

export const documentTypesApi = {
  list: () => api.get<DocumentType[]>('/api/v1/document-types/'),
  get: (id: string) => api.get<DocumentType>(`/api/v1/document-types/${id}`),
  create: (data: DocumentTypeCreate) => api.post<DocumentType>('/api/v1/document-types/', data),
  update: (id: string, data: DocumentTypeUpdate) => api.put<DocumentType>(`/api/v1/document-types/${id}`, data),
  delete: (id: string) => api.delete(`/api/v1/document-types/${id}`),
};

// ── Collaborateurs ──────────────────────────────────────────────

export const collaborateursApi = {
  list: (params?: Record<string, any>) =>
    api.get<PaginatedResponse<CollaborateurWithStats>>('/api/v1/collaborateurs/', { params }),

  get: (id: string) =>
    api.get<CollaborateurWithStats>(`/api/v1/collaborateurs/${id}`),

  create: (data: Partial<Collaborateur>) =>
    api.post('/api/v1/collaborateurs/', data),

  update: (id: string, data: Partial<Collaborateur>) =>
    api.put(`/api/v1/collaborateurs/${id}`, data),

  merge: (targetId: string, sourceId: string) =>
    api.post(`/api/v1/collaborateurs/${targetId}/merge/${sourceId}`),
};

// ── RGPD ────────────────────────────────────────────────────────

export const rgpdApi = {
  listAlerts: (params?: Record<string, any>) =>
    api.get<PaginatedResponse<RGPDAlert>>('/api/v1/rgpd/alerts', { params }),

  summary: () =>
    api.get<AlertsSummary>('/api/v1/rgpd/alerts/summary'),

  action: (
    alertId: string,
    action: string,
    postponeDays?: number,
    deleteDocument?: boolean,
    deleteScanSource?: boolean,
  ) =>
    api.post(`/api/v1/rgpd/alerts/${alertId}/action`, {
      action,
      postpone_days: postponeDays,
      delete_document: deleteDocument,
      delete_scan_source: deleteScanSource ?? false,
    }),
};

// ── Stats ───────────────────────────────────────────────────────

export const statsApi = {
  dashboard: () => api.get<DashboardStats>('/api/v1/stats/dashboard'),
};

// ── Scan Sources ─────────────────────────────────────────────────

export const scanSourcesApi = {
  list: () =>
    api.get<ScanSource[]>('/api/v1/scan-sources/'),

  create: (data: ScanSourceCreate) =>
    api.post<ScanSource>('/api/v1/scan-sources/', data),

  update: (id: string, data: Partial<ScanSourceCreate>) =>
    api.put<ScanSource>(`/api/v1/scan-sources/${id}`, data),

  delete: (id: string) =>
    api.delete(`/api/v1/scan-sources/${id}`),

  trigger: (id: string) =>
    api.post<{ status: string; task_id: string }>(`/api/v1/scan-sources/${id}/trigger`),
};

// ── App Settings ────────────────────────────────────────────────

export const settingsApi = {
  get: () => api.get<AppSettings>('/api/v1/settings/'),
  update: (data: Partial<AppSettings>) => api.put<AppSettings>('/api/v1/settings/', data),
};

// ── Users ────────────────────────────────────────────────────────

export const usersApi = {
  list: () => api.get<User[]>('/api/v1/users/'),
  create: (data: UserCreate) => api.post<User>('/api/v1/users/', data),
  update: (id: string, data: UserUpdate) => api.put<User>(`/api/v1/users/${id}`, data),
  delete: (id: string) => api.delete(`/api/v1/users/${id}`),
};

// ── RAG (chatbot documentaire) ───────────────────────────────────

export const ragApi = {
  status: () => api.get<{ enabled: boolean }>('/api/v1/rag/status'),
};

export default api;
